import { authFetch } from './authFetch';
import { API_BASE } from './version';
import type { PendingWorkItem } from '../../../../shared/lib/workStatus';
import type { LiveAgentActivity, GitHubEventItem } from '../../../../shared/types/epics';

export interface PendingWorkResponse {
  items: PendingWorkItem[];
  generatedAt: string;
}

export interface LiveActivityResponse {
  agents: LiveAgentActivity[];
  generatedAt: string;
}

/**
 * GET /api/v1/activity/live — agents running right now across the requester's
 * scope (epic-planner / product-manager and story agents), derived from live
 * heartbeats. Fills the gap where a mid-run agent has no SESSION# row yet.
 */
export async function fetchLiveAgents(): Promise<LiveActivityResponse> {
  const response = await authFetch(`${API_BASE}/activity/live`, { method: 'GET' });
  if (!response.ok) {
    throw new Error(`Failed to fetch live activity: ${response.statusText}`);
  }
  const body = (await response.json()) as { data: LiveActivityResponse };
  return body.data;
}

/**
 * GET /api/v1/activity/pending — everything non-terminal that is NOT visible
 * as a running session: waiting, stalled, launch-failed, crashed-at-startup,
 * operator-action-required, approval gates. (FORGE-087)
 */
export async function fetchPendingWork(): Promise<PendingWorkResponse> {
  const response = await authFetch(`${API_BASE}/activity/pending`, { method: 'GET' });
  if (!response.ok) {
    throw new Error(`Failed to fetch pending activity: ${response.statusText}`);
  }
  const body = (await response.json()) as { data: PendingWorkResponse };
  return body.data;
}

export interface GitHubEventsResponse {
  events: GitHubEventItem[];
  nextCursor: string | null;
}

/**
 * GET /api/v1/activity/github-events — Forge's webhook activity log
 * (GHEVENT# rows written by recordWebhookEvent(), added 2026-07-16). One row
 * per webhook delivery for `repo`, whatever happened to it: accepted,
 * dropped because the repo isn't attached to a project, an unhandled
 * event/action, or an error. This is what surfaces "yes, we got your
 * comment" — and why nothing happened, if nothing did.
 */
export async function fetchGitHubEvents(
  repo: string,
  opts: { limit?: number; cursor?: string } = {},
): Promise<GitHubEventsResponse> {
  const qs = new URLSearchParams({ repo });
  if (opts.limit !== undefined) qs.set('limit', String(opts.limit));
  if (opts.cursor) qs.set('cursor', opts.cursor);

  const response = await authFetch(`${API_BASE}/activity/github-events?${qs.toString()}`, { method: 'GET' });
  if (!response.ok) {
    throw new Error(`Failed to fetch GitHub event activity: ${response.statusText}`);
  }
  const body = (await response.json()) as { data: GitHubEventsResponse };
  return body.data;
}
